import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { CustomerOrders } from "@/components/customers/CustomerOrders";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCustomer, useDeleteCustomer, useUpdateCustomer } from "@/hooks/use-customers";
import { formatDate } from "@/lib/format";

export function CustomerDetailPage() {
  const { customerId } = useParams({ strict: false }) as { customerId: string };
  const navigate = useNavigate();
  const { data: customer, isLoading, isError, error, refetch } = useCustomer(customerId);
  const updateCustomer = useUpdateCustomer(customerId);
  const deleteCustomer = useDeleteCustomer();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load customer"} onRetry={() => refetch()} />
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Customer not found.</p>
        <Link to="/dashboard/customers" className="text-sm text-primary hover:underline mt-2 block">
          Back to customers
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/dashboard/customers"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Customers
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-2xl font-bold tracking-tight">{customer.name}</h1>
        {!editing && <StatusBadge status={customer.status} />}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Details</CardTitle>
          {!editing && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <CustomerForm
              defaultValues={customer}
              isSaving={updateCustomer.isPending}
              onCancel={() => setEditing(false)}
              onSubmit={(values) =>
                updateCustomer.mutate(values, { onSuccess: () => setEditing(false) })
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Email</span>
                <p className="mt-1">{customer.email}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <div className="mt-1">
                  <StatusBadge status={customer.status} />
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Created</span>
                <p className="mt-1">{formatDate(customer.createdAt)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <CustomerOrders orders={customer.orders} />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete customer"
        description={`Delete ${customer.name}? Their orders will be removed too. This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteCustomer.isPending}
        onConfirm={() =>
          deleteCustomer.mutate(customer.id, {
            onSuccess: () => navigate({ to: "/dashboard/customers" }),
          })
        }
      />
    </div>
  );
}
